import { useMutation, useQueryClient } from '@tanstack/react-query';
import { confirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import {
  clearLogData,
  type ClearLogDataResponse,
} from '../../../services/bot-sources/LogUploadService';
import { LOG_UPLOADS } from '../../query/bot-sources/keys';
import {
  GET_BOTS_ACTIVITY_CONTENT,
  GET_BOTS_ACTIVITY_OVERVIEW,
  GET_BOTS_ACTIVITY_STATUS,
  GET_BOTS_ACTIVITY_TIMESERIES,
} from '../../query/bots-activity/keys';

/**
 * Wipes all CSV upload history + derived crawl rows for a project. Exposes a
 * `clearWithConfirm()` helper that opens a PrimeReact confirm dialog before
 * firing — destructive operation, so we never call it without an explicit
 * user yes.
 *
 * The page hosting this hook must mount `<ConfirmDialog className="dialog-box" />`
 * somewhere in the tree (same contract as `useDisconnectCloudflare`).
 *
 * On success: invalidates `LOG_UPLOADS` and all four `GET_BOTS_ACTIVITY_*`
 * keys so the UI reflects the freshly-empty state without a refresh.
 */
export const useClearLogData = (projectId: number) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutation = useMutation<ClearLogDataResponse, Error, void>({
    mutationFn: () => clearLogData(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOG_UPLOADS, projectId] });
      queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_OVERVIEW, projectId] });
      queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_TIMESERIES, projectId] });
      queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_CONTENT, projectId] });
      queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_STATUS, projectId] });
    },
  });

  const clearWithConfirm = (onSuccess?: (result: ClearLogDataResponse) => void) => {
    confirmDialog({
      message: t('botsActivity.clearLogDataConfirmMessage'),
      header: t('botsActivity.clearLogDataConfirmHeader'),
      defaultFocus: 'reject',
      accept: () => {
        mutation.mutate(undefined, { onSuccess });
      },
    });
  };

  return {
    clearWithConfirm,
    isPending: mutation.isPending,
    isError: mutation.isError,
  };
};
